import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  ClockIcon,
  EyeIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import { buildPracticeLaunchUrl } from '../lib/practiceAssignments';
import { buildPracticeHistoryPath } from '../lib/practiceAttempts';
import { buildIeltsSpeakingLaunchUrl, formatIeltsBand, ieltsBandFromAttempt } from '../lib/ieltsSpeaking';
import {
  aggregateLegacyQuizResults,
  completedPracticeReviews,
  practiceAccuracyPercent,
  practiceMeaningLabels,
} from '../lib/reviewHistory';

const API = '/api';

const PRACTICE_LABELS = {
  dictation: 'Dictation Loop',
  meaning_sprint: '7-Second Meaning',
  sudden_sprint: 'Lightning Challenge',
  pronunciation: 'Pronunciation Practice',
  adaptive_reading: 'Adaptive Reading',
  ielts_speaking: 'IELTS Speaking Practice',
};

const formatToBeijingTime = (value, locale) => {
  if (!value) return '';
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return new Intl.DateTimeFormat(locale === 'zh-CN' ? 'zh-CN' : 'en', {
      timeZone: 'Asia/Shanghai',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch (_) {
    return String(value);
  }
};

const authHeaders = (json = false) => ({
  ...(json ? { 'Content-Type': 'application/json' } : {}),
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const fetchJson = async (url) => {
  const response = await fetch(url, { headers: authHeaders() });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.message || data.error || 'Review history could not be loaded.');
  return data;
};

export default function ReviewPage() {
  const { locale, t } = useI18n();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState([]);
  const [practices, setPractices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [starting, setStarting] = useState('');

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setWarning('');
    const [practiceResult, quizResult] = await Promise.allSettled([
      fetchJson(`${API}/student/practice-assignments?limit=200`),
      fetchJson(`${API}/results`),
    ]);

    if (practiceResult.status === 'fulfilled') {
      setPractices(completedPracticeReviews(practiceResult.value?.practices));
    } else {
      setPractices([]);
    }
    if (quizResult.status === 'fulfilled') {
      setQuizzes(aggregateLegacyQuizResults(quizResult.value));
    } else {
      setQuizzes([]);
    }

    const failures = [practiceResult, quizResult].filter((result) => result.status === 'rejected');
    if (failures.length === 2) {
      setError(t('Review history could not be loaded.'));
    } else if (failures.length === 1) {
      setWarning(t('Some review history could not be loaded.'));
    }
    setIsLoading(false);
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const startPractice = async (item) => {
    const assignmentId = item?.assignment?.id;
    if (!assignmentId || starting) return;
    if (item.assignment?.practice_type === 'ielts_speaking') {
      navigate(buildIeltsSpeakingLaunchUrl({
        payload: { assignment_id: assignmentId, target_id: item.id || '' },
      }));
      return;
    }
    setStarting(assignmentId);
    setError('');
    try {
      const response = await fetch(`/api/student/practice-assignments/${assignmentId}/start`, {
        method: 'POST',
        headers: authHeaders(true),
        body: JSON.stringify({}),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || t('The practice could not be opened.'));
      navigate(buildPracticeLaunchUrl(data.launch));
    } catch (startError) {
      setError(startError.message || t('The practice could not be opened.'));
    } finally {
      setStarting('');
    }
  };

  if (isLoading) return <p className="p-8 text-center text-gray-600">{t('Loading review history...')}</p>;

  return (
    <main className="mx-auto max-w-5xl p-4 md:p-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('Review')}</div>
          <h1 className="mt-1 text-3xl font-bold text-gray-900">{t('Review history')}</h1>
          <p className="mt-2 text-gray-600">{t('Your completed teacher-assigned practice and earlier quizzes are kept here.')}</p>
        </div>
        <button
          type="button"
          onClick={load}
          className="rounded border border-gray-300 p-2 text-gray-600 hover:bg-white"
          title={t('Refresh')}
          aria-label={t('Refresh')}
        >
          <ArrowPathIcon className="h-5 w-5" />
        </button>
      </header>

      {error && <div className="mb-5 rounded border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800">{error}</div>}
      {warning && <div className="mb-5 rounded border border-amber-200 bg-amber-50 px-4 py-3 text-amber-900">{warning}</div>}

      <section>
        <div className="flex items-end justify-between gap-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{t('Assigned practice')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('Review the exact meanings, latest result, and practice again when needed.')}</p>
          </div>
          <Link to="/student/quizzes" className="text-sm font-semibold text-indigo-700 hover:underline">
            {t('Open My Practices')}
          </Link>
        </div>

        {practices.length === 0 ? (
          <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-6 text-center text-gray-500">
            {t('No completed assigned practice yet.')}
          </div>
        ) : (
          <ul className="mt-4 grid gap-4 md:grid-cols-2">
            {practices.map((item) => {
              const assignment = item.assignment || {};
              const attempt = item.latest_attempt || {};
              const accuracy = practiceAccuracyPercent(item);
              const isIelts = assignment.practice_type === 'ielts_speaking';
              const predictedBand = isIelts ? ieltsBandFromAttempt(attempt) : null;
              const meanings = practiceMeaningLabels(item);
              const totalMeanings = assignment.source?.items_snapshot?.length || 0;
              const remainingMeanings = Math.max(0, totalMeanings - meanings.length);
              return (
                <li key={item.id || assignment.id} className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-indigo-700">
                        <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                        {t(PRACTICE_LABELS[assignment.practice_type] || 'Practice')}
                      </div>
                      <h3 data-i18n-skip className="mt-1 truncate text-lg font-bold text-gray-900">{assignment.title || t('Practice')}</h3>
                    </div>
                    {(isIelts ? predictedBand != null : accuracy != null) && (
                      <div className="shrink-0 rounded bg-emerald-50 px-3 py-1 text-lg font-bold text-emerald-700" data-i18n-skip>{isIelts ? formatIeltsBand(predictedBand) : `${accuracy}%`}</div>
                    )}
                  </div>

                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-500">
                    <span>{t('{count} attempts', { count: item.attempt_count || 0 })}</span>
                    <span>{isIelts ? (assignment.config?.question ? t('Prompt provided') : t('General speaking assessment')) : t('{count} exact meanings', { count: totalMeanings })}</span>
                    <span className="inline-flex items-center gap-1">
                      <ClockIcon className="h-4 w-4" />
                      {formatToBeijingTime(item.review_timestamp || attempt.completed_at, locale)}
                    </span>
                  </div>

                  {meanings.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {meanings.map((label, index) => (
                        <span data-i18n-skip key={`${assignment.id || item.id}:${index}:${label}`} className="rounded border border-gray-200 bg-gray-50 px-2 py-1 text-sm text-gray-700">{label}</span>
                      ))}
                      {remainingMeanings > 0 && <span className="px-2 py-1 text-sm text-gray-500">+{remainingMeanings}</span>}
                    </div>
                  )}

                  <div className="mt-5 flex items-center justify-between gap-3 border-t border-gray-100 pt-4">
                    <span className="text-sm font-medium text-emerald-700">{t('Completed')}</span>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      <Link
                        to={isIelts
                          ? buildIeltsSpeakingLaunchUrl({ payload: { assignment_id: assignment.id, target_id: item.id || '', history: '1' } })
                          : buildPracticeHistoryPath(assignment.id)}
                        className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"
                      >
                        <EyeIcon className="h-4 w-4" aria-hidden="true" />
                        {t('View all attempts')}
                      </Link>
                      <button
                        type="button"
                        onClick={() => startPractice(item)}
                        disabled={starting === assignment.id}
                        className="inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-50"
                      >
                        {starting === assignment.id ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlayIcon className="h-4 w-4" />}
                        {t('Practice again')}
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section className="mt-10 border-t border-gray-200 pt-8">
        <h2 className="text-xl font-bold text-gray-900">{t('Past quizzes')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('Open an earlier quiz to review each saved attempt.')}</p>
        {quizzes.length === 0 ? (
          <div className="mt-4 rounded border border-gray-200 bg-gray-50 p-6 text-center text-gray-500">
            {t('No past quizzes yet.')}
          </div>
        ) : (
          <ul className="mt-4 space-y-3">
            {quizzes.map((quiz) => (
              <li key={quiz.quiz_id} className="rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md">
                <Link to={`/review/${quiz.quiz_id}`} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <p data-i18n-skip className="text-lg font-semibold text-gray-800">{quiz.quiz_name}</p>
                    <p className="mt-1 text-sm text-gray-600">
                      {t('{count} attempts', { count: quiz.attempt_count })} · {t('Last on {date}', { date: formatToBeijingTime(quiz.last_attempt_ts, locale) })}
                    </p>
                  </div>
                  <ChevronRightIcon className="h-6 w-6 shrink-0 text-gray-400" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}
